<template>
  <Teleport :to="port">
    <div
      class="modal-container fade-out"
      :class="[type, { 'clickable-outside': props.closeOnClickOutside }]"
      @keyup.esc="closeModal"
    >
      <transition
        :name="`${type}-animation-content`"
        mode="out-in"
        @after-leave="emit('closeModal')"
      >
        <div v-if="showModal" ref="modalContent" class="modal-content">
          <div v-if="title || closeButtonVisible" class="modal-header">
            <span
              v-if="type === 'mobile' && swipeVisible"
              v-touch:swipe.bottom="closeModal"
              class="modal-header__swipe"
            />
            <span class="modal-header__title">
              {{ title }}
            </span>
            <span
              v-if="closeButtonVisible"
              class="modal-header__close-icon"
              @click="closeModal"
            >
              <BaseSvg :src="CloseIcon" />
            </span>
          </div>
          <div class="body-container">
            <div v-if="showTopShadow" class="box-shadow box-shadow-top" />
            <div v-if="showBottomShadow" class="box-shadow box-shadow-bottom" />
            <div
              v-if="!!slots.body"
              ref="modalBody"
              class="modal-body"
              @scroll="checkShow"
            >
              <slot name="body" />
            </div>
          </div>
          <div v-if="!!slots.footer" class="modal-footer">
            <slot name="footer" />
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { useClickOutside } from "@/composables";
import {
  ref,
  onBeforeUnmount,
  useSlots,
  onMounted,
  nextTick,
  useTemplateRef,
} from "vue";
import { CloseIcon } from "@/assets/icons";
import BaseSvg from "./BaseSvg.vue";

export interface IModalProps {
  width?: string;
  title?: string;
  closeButtonVisible?: boolean;
  zIndex?: string;
  closeOnClickOutside?: boolean;
  closeOnEsc?: boolean;
  type?: "modal" | "drawer" | "mobile";
  port?: string;
  swipeVisible?: boolean;
  maxHeight?: string;
}

const props = withDefaults(defineProps<IModalProps>(), {
  width: "480px",
  zIndex: "100",
  closeButtonVisible: true,
  closeOnClickOutside: false,
  closeOnEsc: false,
  type: "drawer",
  port: "body",
  swipeVisible: true,
  maxHeight: "60vh",
});
const emit = defineEmits(["closeModal"]);
const slots = useSlots();
const modalWidth = ref(props.width);
const modalMaxHeight = ref(props.maxHeight);
const modalZIndex = ref(props.zIndex);
const showModal = ref(false);
const modalContentRef = useTemplateRef<HTMLDivElement>("modalContent");
const refModalBody = useTemplateRef<HTMLDivElement>("modalBody");
const showTopShadow = ref<boolean>(false);
const showBottomShadow = ref<boolean>(false);

const closeModal = () => {
  showModal.value = false;
};

useClickOutside(modalContentRef, () => {
  if (props.closeOnClickOutside) {
    closeModal();
  }
});
const closeWithEsc = ({ key }: any) => {
  if (key === "Escape") {
    closeModal();
  }
};

const checkShow = () => {
  const element = refModalBody.value;
  if (element) {
    const { scrollHeight, clientHeight, offsetHeight, scrollTop } = element;
    if (scrollHeight === clientHeight) return;
    if (scrollTop + offsetHeight === scrollHeight) {
      showTopShadow.value = true;
      showBottomShadow.value = false;
    } else if (scrollTop === 0) {
      showTopShadow.value = false;
      showBottomShadow.value = true;
    } else {
      showTopShadow.value = true;
      showBottomShadow.value = true;
    }
  }
};
onMounted(() => {
  showModal.value = true;
  if (props.closeOnEsc) {
    document.addEventListener("keydown", closeWithEsc);
  }
  nextTick(() => {
    checkShow();
  });
});

onBeforeUnmount(() => {
  if (props.closeOnEsc) {
    document.removeEventListener("keydown", closeWithEsc);
  }
});
defineExpose({ closeModal });
</script>

<style lang="scss" src="./styles/modal.scss" scoped />

<style lang="scss" scoped>
.modal-container {
  z-index: v-bind(modalZIndex);
}
.modal-content {
  width: v-bind(modalWidth);
  & .modal-body {
    max-height: v-bind(modalMaxHeight);
  }
}
</style>
